import React, { useEffect, useState } from "react";
import "./VerifyEmail.scss";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  appData,
  authOtp,
  authResendPasswordOtp,
  authSetPassword,
} from "../../../../core/axios/services";
import {
  clearPendingSignupData,
  getApiErrorCode,
  getApiErrorMessage,
  getPostAuthRoute,
  getPendingSignupEmail,
} from "../../../../services/helpers";
import { addUser, addUserToken } from "../../../../redux/actions/user";
import TextInputLabel from "../../../components/Comon/TextInput/TextInput";
import Logo from "../../../components/Comon/Logo/Logo";
import quote from "../../../../../src/quote.svg";
import brock from "../../../../../src/Brock_Headshot.jpg";

const VerifyEmail = ({ showGlobalSnackbar }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const logo = "/assets/logotm.png";
  const [testimonials, setTestimonials] = useState([]);
  const [testimonial, setTestimonial] = useState({});
  const [password, setPassword] = useState("");
  const [passwordRepeat, setPasswordRepeat] = useState("");
  const [status, setStatus] = useState("verifying");
  const [tokenError, setTokenError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isPasswordSetupFlow, setIsPasswordSetupFlow] = useState(false);

  const token = searchParams.get("otp") || params.token || "";
  const email = searchParams.get("email") || getPendingSignupEmail();

  useEffect(() => {
    collectAppData().then((data) => {
      setTestimonials([
        {
          name: data.landingData.testimonial_1_name,
          job: data.landingData.testimonial_1_job,
          image: data.landingData.testimonial_1_image,
          text: data.landingData.testimonial_1_text,
        },
        {
          name: data.landingData.testimonial_2_name,
          job: data.landingData.testimonial_2_job,
          image: data.landingData.testimonial_2_image,
          text: data.landingData.testimonial_2_text,
        },
        {
          name: data.landingData.testimonial_3_name,
          job: data.landingData.testimonial_3_job,
          image: data.landingData.testimonial_3_image,
          text: data.landingData.testimonial_3_text,
        },
      ]);
    });
  }, []);

  useEffect(() => {
    if (!token || !email) {
      setStatus("invalid");
      setTokenError(i18n.t("missingVerificationDetails"));
    }
  }, [email, i18n, token]);

  useEffect(() => {
    if (!token || !email || status !== "verifying") {
      return;
    }

    let isMounted = true;

    const verifyLink = async () => {
      try {
        setLoading(true);
        const response = await authOtp(email, token);

        if (!isMounted) {
          return;
        }

        handleAuthSuccess(response?.data?.data);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        if (getApiErrorCode(error) === "PASSWORD_SETUP_REQUIRED") {
          setIsPasswordSetupFlow(true);
          setStatus("form");
          setTokenError("");
          return;
        }

        setStatus("invalid");
        setTokenError(getApiErrorMessage(error, i18n.t("thereWasAnErrorSingle")));
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    verifyLink();

    return () => {
      isMounted = false;
    };
  }, [email, i18n, status, token]);

  useEffect(() => {
    if (testimonials.length === 0) {
      return;
    }

    const randomNumber = Math.floor(Math.random() * testimonials.length);
    setTestimonial(testimonials[randomNumber]);
  }, [testimonials]);

  const collectAppData = async () => {
    return appData().then((res) => res.data.data);
  };

  const handleAuthSuccess = (authData) => {
    const authUser = authData?.user ?? authData;
    const accessToken =
      authData?.accessToken || authData?.access_token || authData?.token;

    if (!authUser || !accessToken) {
      const message = i18n.t("thereWasAnErrorSingle");
      setStatus("invalid");
      setTokenError(message);
      showGlobalSnackbar(message);
      return;
    }

    localStorage.setItem("loginEmail", email);
    dispatch(addUserToken({ ...authData, accessToken }));
    dispatch(addUser(authUser));

    const nextRoute = getPostAuthRoute(authUser);

    if (authUser?.clinic) {
      clearPendingSignupData();
    }

    navigate(nextRoute);
  };

  const handleSubmit = async () => {
    if (!token || !email) {
      const message = i18n.t("missingVerificationDetails");
      setStatus("invalid");
      setTokenError(message);
      showGlobalSnackbar(message);
      return;
    }

    if (password === "" || passwordRepeat === "") {
      showGlobalSnackbar(i18n.t("completeAllFields"));
      return;
    }

    if (passwordRepeat !== password) {
      showGlobalSnackbar(i18n.t("pleaseInsertConfirmMessage"));
      return;
    }

    if (password.length < 6) {
      showGlobalSnackbar(i18n.t("passwordRule"));
      return;
    }

    try {
      setLoading(true);
      const response = await authSetPassword(token, password, passwordRepeat);
      setTokenError("");
      handleAuthSuccess(response?.data?.data);
    } catch (error) {
      const errorCode = getApiErrorCode(error);
      const message = getApiErrorMessage(error, i18n.t("thereWasAnErrorSingle"));
      setStatus("invalid");
      setTokenError(
        errorCode === "PASSWORD_ALREADY_SET"
          ? i18n.t("passwordAlreadySet")
          : message
      );
      showGlobalSnackbar(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      const message = i18n.t("missingVerificationDetails");
      showGlobalSnackbar(message);
      return;
    }

    try {
      setLoading(true);
      await authResendPasswordOtp(email);
      setStatus("form");
      setTokenError("");
      showGlobalSnackbar(i18n.t("passwordSetupResendSuccess"));
    } catch (error) {
      showGlobalSnackbar(getApiErrorMessage(error, i18n.t("thereWasAnErrorSingle")));
    } finally {
      setLoading(false);
    }
  };

  const renderPrimaryState = () => {
    if (status === "verifying") {
      return (
        <div className="verifyEmailState">
          <h2>{i18n.t("pleaseWait")}</h2>
          <p>{i18n.t("verifyingEmailLink")}</p>
        </div>
      );
    }

    if (status === "invalid") {
      return (
        <div className="verifyEmailState">
          <h2>{i18n.t("verificationLinkInvalid")}</h2>
          <p>{i18n.tokenError || t("verificationLinkInvalidDescription")}</p>

          <p>
            {i18n.t("mistakeContactSupport")}
            <a href="mailto: support@chiropracticoutcomes.com ">
              {i18n.t("mistakeContactSupportEmail")}
            </a>
          </p>

          <div className="verifyEmailActions">
            {isPasswordSetupFlow ? (
              <button
                className="primaryButton"
                onClick={handleResend}
                disabled={loading || !email}
              >
                {i18n.t("resendVerificationEmail")}
              </button>
            ) : null}
            <button
              className="secondaryButton"
              onClick={() => navigate("/login")}
            >
              {i18n.t("backToLogin")}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="verifyEmailForm">
        <h2>{i18n.t("completeAccountSetup")}</h2>
        <p className="supportText">
          {i18n.t("signupEmailSentDescription")} <strong>{email}</strong>.
        </p>
        <TextInputLabel
          type={"password"}
          label={i18n.t("createPassword")}
          value={password}
          setValue={setPassword}
          placeholder={i18n.t("enterYourPassword")}
          onKeyDownEvent={handleSubmit}
        />
        <div className="passwordRequire">
          <p>{i18n.t("passwordConsistOf")}</p>
          <ul>
            <li>{i18n.t("passwordMinimum")}</li>
            <li>{i18n.t("upperlLower")}</li>
            <li>{i18n.t("passowrdNumbers")}</li>
          </ul>
        </div>
        <div className="passwordRequireMob">
          <p>{i18n.t("mobilePasswordMust")}</p>
        </div>
        <TextInputLabel
          type={"password"}
          label={i18n.t("confirmPassword")}
          value={passwordRepeat}
          setValue={setPasswordRepeat}
          placeholder={i18n.t("enterYourPasswordConfirm")}
          onKeyDownEvent={handleSubmit}
        />
        <div className="privacyTermsLink">
          <p>
            {i18n.t("readPolicy")} <a href="http://chiropracticoutcomes.com/privacy">{i18n.t("privacypolicy")}</a>{" "}
            {i18n.t("and")} <a href="http://chiropracticoutcomes.com/terms">{i18n.t("terms")}</a>
          </p>
        </div>
        <div className="verifyEmailActions">
          <button
            className="primaryButton"
            onClick={handleSubmit}
            disabled={loading}
          >
            {i18n.t("completeSetup")}
          </button>
          <button className="secondaryButton" onClick={handleResend} disabled={loading}>
            {i18n.t("resendVerificationEmail")}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="verifyEmailWrapper">
      <a href="https://chiropracticoutcomes.com/" style={{ cursor: "pointer" }}>
        <Logo className="logo" src={logo} />
      </a>
      <div className="verifyEmailContent">
        <div className="verifyEmailCard">{renderPrimaryState()}</div>
        <div className="verifyEmailTestimonials">
          <div className="testimonialContent">
            <img className="quote" src={quote} alt="quote" />
            <p>{i18n.testimonial?.text || t("signUpTestimonialDes")}</p>
            <span></span>
            <div className="testimonialName">
              <img src={brock} alt="testimonial" />
              <div>
                <div className={"testimonial-name"}>
                  {i18n.testimonial?.name || t("signUpTestimonialName")}
                </div>
                <div className={"testimonial-job"}>
                  {i18n.testimonial?.job || t("signUpTestimonialJob")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
